import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    fetchProductByHandle,
    fetchProductRecommendations,
    formatShopifyPrice,
} from "../lib/shopify";
import { useCart } from "../lib/CartContext";
import { usePirate } from "../lib/PirateContext";
import ProductCard from "../components/ProductCard";
import ProductImageGallery from "../components/ProductImageGallery";
import ReviewSection from "../components/ReviewSection";
import {
    JudgeMeProvider,
    JudgeMeSummary,
    JudgeMeSection,
} from "../components/JudgeMeReviews";
import NotifyMeButton from "../components/NotifyMeButton";
import { ArrowLeft, Plus, Minus, ShieldCheck, Truck, Package, Sparkles } from "lucide-react";

export default function ProductDetail() {
    const { handle } = useParams();
    const [product, setProduct] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const [qty, setQty] = useState(1);
    const [loading, setLoading] = useState(true);
    const { addItem, loading: cartLoading } = useCart();
    const { award } = usePirate();

    useEffect(() => {
        setLoading(true);
        setRecommendations([]);
        fetchProductByHandle(handle)
            .then((p) => {
                setProduct(p);
                if (p) {
                    award("viewProduct", { productId: p.id });
                    // Fire-and-forget recommendations
                    fetchProductRecommendations(p.id)
                        .then((recs) => setRecommendations(recs.slice(0, 4)))
                        .catch(() => {});
                }
            })
            .catch((e) => console.error(e))
            .finally(() => setLoading(false));
    }, [handle, award]);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 text-center">
                <div className="font-hand text-3xl text-bounty animate-pulse">
                    caricamento dal Grand Line...
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="max-w-3xl mx-auto px-5 lg:px-10 py-20 text-center">
                <div className="bounty-card p-10">
                    <div className="font-hand text-3xl text-bounty">
                        Nessuna figura trovata
                    </div>
                    <p className="mt-3 text-ink/70">
                        Questa figura non è più disponibile nel nostro store.
                    </p>
                    <Link
                        to="/figure"
                        className="btn-manga btn-manga-red mt-6 inline-flex"
                    >
                        Torna al bottino
                    </Link>
                </div>
            </div>
        );
    }

    async function handleAdd() {
        await addItem(product, qty);
        award("addToCart", { productId: product.id });
    }

    return (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10" data-testid="product-detail-page">
            <Link
                to="/figure"
                className="inline-flex items-center gap-2 font-mono text-sm tracking-widest hover:text-bounty"
            >
                <ArrowLeft size={14} /> torna al bottino
            </Link>

            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border-2 border-ink bg-gold shadow-manga font-mono text-xs"
                data-testid="xp-hint-banner"
            >
                <Sparkles size={14} /> Aggiungi al bottino: +20 XP · +25 Berry
            </motion.div>

            <JudgeMeProvider externalId={product.externalId} handle={product.handle}>
            <div className="grid lg:grid-cols-2 gap-12 mt-6">
                {/* Image gallery */}
                <ProductImageGallery
                    images={
                        product.images && product.images.length > 0
                            ? product.images
                            : [{ url: product.image, alt: product.character }]
                    }
                    productTitle={product.character}
                />

                {/* Info */}
                <div>
                    <div className="font-mono text-xs tracking-widest uppercase text-ink/60">
                        {product.crew}
                    </div>
                    <h1 className="font-display text-5xl lg:text-6xl tracking-tight leading-[0.95] mt-2">
                        {product.character}
                    </h1>

                    <JudgeMeSummary
                        className="mt-3"
                    />

                    <div className="mt-6 flex items-center gap-3 flex-wrap">
                        <span className="px-3 py-1 border-2 border-ink bg-ink text-parchment font-hand text-base rotate-stamp inline-block">
                            {product.rarity}
                        </span>
                        <span
                            className={`font-mono text-xs tracking-widest ${product.available ? "text-ink/60" : "text-bounty font-bold"}`}
                        >
                            {product.available
                                ? `stock: ${product.stock || "disponibile"}`
                                : "SOLD OUT"}
                        </span>
                    </div>

                    <div className="mt-8 p-6 bounty-card bg-parchment">
                        <div className="font-hand text-bounty text-2xl leading-none">
                            taglia
                        </div>
                        <div className="font-display text-6xl lg:text-7xl text-ink leading-none mt-1" data-testid="product-price">
                            {formatShopifyPrice(product.price, product.currency)}
                        </div>
                        <div className="font-mono text-xs text-ink/60 mt-1">
                            Bounty: {product.bounty}
                        </div>
                    </div>

                    {product.description && (
                        <p className="mt-6 text-ink/80 leading-relaxed whitespace-pre-wrap">
                            {product.description}
                        </p>
                    )}

                    <div className="mt-7 grid grid-cols-3 gap-3">
                        <div className="bounty-card p-3 text-center">
                            <div className="font-hand text-xs text-bounty">altezza</div>
                            <div className="font-mono text-lg">{product.height_cm}cm</div>
                        </div>
                        <div className="bounty-card p-3 text-center">
                            <div className="font-hand text-xs text-bounty">scala</div>
                            <div className="font-mono text-lg">{product.scale}</div>
                        </div>
                        <div className="bounty-card p-3 text-center">
                            <div className="font-hand text-xs text-bounty">brand</div>
                            <div className="font-mono text-xs leading-tight pt-1">
                                {product.manufacturer}
                            </div>
                        </div>
                    </div>

                    {/* qty + add */}
                    <div className="mt-8 flex items-center gap-4">
                        <div className="flex items-center border-2 border-ink shadow-manga bg-white">
                            <button
                                onClick={() => setQty(Math.max(1, qty - 1))}
                                className="w-11 h-12 hover:bg-bounty hover:text-parchment"
                                data-testid="qty-decrease-btn"
                            >
                                <Minus size={16} className="mx-auto" />
                            </button>
                            <span className="px-5 font-mono text-xl" data-testid="qty-value">
                                {qty}
                            </span>
                            <button
                                onClick={() => setQty(qty + 1)}
                                className="w-11 h-12 hover:bg-bounty hover:text-parchment"
                                data-testid="qty-increase-btn"
                            >
                                <Plus size={16} className="mx-auto" />
                            </button>
                        </div>
                        <button
                            onClick={handleAdd}
                            disabled={!product.available || cartLoading}
                            className="btn-manga btn-manga-red flex-1 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="add-to-cart-btn"
                        >
                            {cartLoading
                                ? "Aggiungo..."
                                : product.available
                                  ? "Aggiungi al Bottino ⚓"
                                  : "Non disponibile"}
                        </button>
                    </div>

                    {/* Notify me when back in stock */}
                    <NotifyMeButton product={product} />

                    {/* Perks */}
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs font-mono">
                        <div className="flex items-center gap-2">
                            <Truck size={16} className="text-bounty" />
                            Spedizione 48h
                        </div>
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-bounty" />
                            Checkout Shopify sicuro
                        </div>
                        <div className="flex items-center gap-2">
                            <Package size={16} className="text-bounty" />
                            Imballo da collezionisti
                        </div>
                    </div>
                </div>
            </div>

            {/* Judge.me reviews (public API widget via backend proxy) */}
            <JudgeMeSection />

            </JudgeMeProvider>

            {/* Reviews (custom internal) */}
            <ReviewSection
                productHandle={product.handle}
                productTitle={product.character || product.name}
            />

            {/* Product recommendations */}
            {recommendations.length > 0 && (
                <section className="mt-24 pt-16 border-t-2 border-ink" data-testid="recommendations-section">
                    <div className="mb-8">
                        <div className="font-hand text-2xl text-bounty dashed-underline inline-block">
                            altri pirati per la tua ciurma
                        </div>
                        <h2 className="font-display text-4xl lg:text-5xl tracking-tight mt-3">
                            POTREBBERO INTERESSARTI
                        </h2>
                    </div>
                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={{
                            hidden: {},
                            visible: {
                                transition: { staggerChildren: 0.08 },
                            },
                        }}
                    >
                        {recommendations.map((p) => (
                            <motion.div
                                key={p.id}
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 },
                                }}
                            >
                                <ProductCard product={p} />
                            </motion.div>
                        ))}
                    </motion.div>
                </section>
            )}
        </div>
    );
}
